<template>
  <div class="order-accordion" :class="{ open: !isCollapsed() }">
    <div
      class="order-accordion__summary"
      :class="{
        collapsed: isCollapsed(),
        expanded: !isCollapsed(),
      }"
      @click="toggleaccordion"
      @keyup.space="toggleaccordion"
      @keyup.enter="toggleaccordion"
      :style="{
        maxHeight: `${height + 35}px`,
      }"
      tabindex="0"
    >
      <div class="order-accordion__background" />

      <div class="order-accordion__value order-accordion__value--from">
        <template v-if="orderStore.isBuyMode">
          <div class="order-accordion__icon">
            <span
              :class="`currency-flag currency-flag-${
                fiatType && fiatType.toLowerCase()
              }`"
            />
          </div>
          <div class="order-accordion__content">
            <h3>
              {{ $t('OrderAccordion.label.spend') }}
              {{ fiatType }}
            </h3>
            <h1>
              {{ toCurrencyFormat(convertFiatAmountToDecimals, fiatType) }}
            </h1>
          </div>
        </template>
        <template v-else>
          <div class="order-accordion__icon">
            <CryptoIcon
              v-if="orderStore.orderAccordionData.coinType"
              :symbol="coinType.toLowerCase()"
              :size="'100%'"
            />
          </div>
          <div class="order-accordion__content">
            <h3>{{ $t('OrderAccordion.label.spend') }} {{ coinType }}</h3>
            <h1>{{ convertCoinToDecimals }}</h1>
          </div>
        </template>
      </div>
      <div class="order-accordion__value order-accordion__value--to">
        <template v-if="orderStore.isBuyMode">
          <div class="order-accordion__icon">
            <CryptoIcon
              v-if="orderStore.orderAccordionData.coinType"
              :symbol="coinType.toLowerCase()"
              :size="'100%'"
            />
          </div>
          <div class="order-accordion__content">
            <h3>{{ $t('OrderAccordion.label.receive') }} {{ coinType }}</h3>
            <h1>{{ convertCoinToDecimals }}</h1>
          </div>
        </template>
        <template v-else>
          <div class="order-accordion__icon">
            <span
              :class="`currency-flag currency-flag-${
                fiatType && fiatType.toLowerCase()
              }`"
            />
          </div>
          <div class="order-accordion__content">
            <h3>
              {{ $t('OrderAccordion.label.receive') }}
              {{ fiatType }}
            </h3>
            <h1>
              {{ toCurrencyFormat(convertFiatAmountToDecimals, fiatType) }}
            </h1>
          </div>
        </template>
      </div>

      <div class="order-accordion__divider" />
      <div class="order-accordion__expanded">
        <ul>
          <li>
            <p class="row">
              {{ orderStore.orderAccordionData.coinAmount }}
              {{ coinType }}
              <small
              >(@{{
                parseFloat(
                  orderStore.orderAccordionData.receivedRate,
                ).toFixed(2)
              }}
                {{ fiatType }})</small
              >
            </p>
            <p class="row">
              {{
                toCurrencyFormat(
                  orderStore.orderAccordionData.quoteValue,
                  fiatType,
                )
              }}&nbsp;
              {{ fiatType }}
            </p>
          </li>
          <li>
            <p>{{ $t('OrderAccordion.label.processingFee') }}</p>
            <p>
              {{
                toCurrencyFormat(
                  orderStore.orderAccordionData.gatewayFee,
                  fiatType,
                )
              }}&nbsp;
              {{ fiatType }}
            </p>
          </li>
          <li>
            <p>{{ $t('OrderAccordion.label.networkFee') }}</p>
            <p>
              {{
                toCurrencyFormat(
                  orderStore.orderAccordionData.networkFee,
                  fiatType,
                )
              }}&nbsp;
              {{ fiatType }}
            </p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script setup>
import {
 ref, onMounted, computed, nextTick,
} from 'vue';
import { whenever } from '@vueuse/core';
import useDecimalHelpers from '@/composables/helpers/useDecimalHelpers';

import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { toCurrencyFormat } from '@/composables/helpers/useStringHelpers';
import CryptoIcon from '@/components/icons/CryptoIcon.vue';

const orderStore = useOrderFormStore();
const { convertToDecimals, convertCoinAmountToDecimals } = useDecimalHelpers;

const height = ref(138); // fixed height of .order-accordion__content
const collapsedHeight = ref(0);
const expandedHeight = ref(0);

const fiatType = computed(() => orderStore.orderAccordionData.fiatType);
const coinType = computed(() => orderStore.orderAccordionData.coinType);
const convertFiatAmountToDecimals = computed(
  () => convertToDecimals(orderStore.orderAccordionData.fiatAmount, 2),
);

const convertCoinToDecimals = computed(
  () => convertCoinAmountToDecimals(orderStore.orderAccordionData.coinAmount, 8),
);

const isCollapsed = () => height.value === collapsedHeight.value;

const toggleaccordion = () => {
  height.value = isCollapsed() ? expandedHeight.value : collapsedHeight.value;
};

const calculateAccordionHeight = () => {
  const summaryEl = document.querySelector('.order-accordion__summary');
  const valueEl = document.querySelector('.order-accordion__value');

  collapsedHeight.value = valueEl?.offsetHeight;
  expandedHeight.value = summaryEl?.offsetHeight + 120; // +120 to account for padding buffer

  height.value = collapsedHeight.value;
};

onMounted(() => {
  setTimeout(() => {
    calculateAccordionHeight();
  }, 1000);
});
whenever(orderStore.orderAccordionData, () => {
  nextTick(() => calculateAccordionHeight());
});
</script>
<style lang="scss">
@import "@/packages/currency-flags/currency";
$flag-dimension: 32px;

.order-accordion {
  width: 100%;
  padding: 0 var(--padding-1) 1.8rem;
  max-width: 54rem;
  margin: 0 auto;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    top: unset;
    bottom: 1.2rem;
    left: 50%;
    width: 7px;
    height: 7px;
    transform-origin: 50% 50%;
    border-top: 3px solid var(--icon-color-default);
    border-right: 3px solid var(--icon-color-default);
    transform: rotate(135deg);
    transition: transform 500ms;
    pointer-events: none;
    @include breakpoint($min-s) {
      width: 10px;
      height: 10px;
      top: 3.2rem;
      right: 4.5rem;
      bottom: unset;
      left: unset;
    }
  }

  &.open:after {
    transform: rotate(-45deg);
  }

  @include breakpoint($min-xs) {
    max-width: 54rem;
    padding: 0 1.8rem;
    padding-bottom: 0;
  }

  &__content {
    h1 {
      @include breakpoint($max-sm) {
        font-size: 1.4rem;
      }
    }
  }

  &__summary {
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    padding: calc(var(--padding-1) / 2);
    transition: max-height 500ms cubic-bezier(0.59, 0.01, 0.46, 0.99);
    position: relative;
    min-height: 7.2rem;
  }

  &__value {
    width: 50%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__icon {
    width: 100%;
    max-width: 2.5rem;
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    aspect-ratio: 1 / 1;

    @include breakpoint($min-s) {
      max-width: 3.2rem;
    }

    .currency-flag {
      width: 100%;
      height: 100%;
      background-position: center;
    }
  }

  &__expanded {
    width: 100%;
    opacity: 0;
    transition: opacity 500ms;

    .expanded & {
      opacity: 1;
    }

    li {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 0;
    }
  }

  &__divider {
    width: 100%;
    height: 1px;
    margin: 1rem 0;
    opacity: 0;
    background: var(--input-background);
    transition: opacity 500ms;

    .expanded & {
      opacity: 1;
    }
  }

  &__background {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: $border-radius;
    background: var(--input-background);
    z-index: -1;
  }
}
</style>
